import { isPlatformBrowser } from '@angular/common';
import {
    Directive,
    effect,
    inject,
    input,
    OnDestroy,
    PLATFORM_ID,
} from '@angular/core';
import { environment } from '../../environments/environment';

@Directive({
    selector: '[dynamicHeaderPage]',
    standalone: true,
})
export class DynamicHeaderPageDirective implements OnDestroy {
    pageDataLoaded = input.required<boolean>();
    dynamicHeaderPage = input.required<boolean>();

    excludedHeaders = input<string[]>([]);

    private readonly platformId = inject(PLATFORM_ID);
    private readonly isBrowser = isPlatformBrowser(this.platformId);

    private boundScrollHandler?: () => void;
    private boundResizeHandler!: () => void;
    private resizeTimeout: any;

    private boundVisibilityHandler!: () => void;

    constructor() {
        if (!this.isBrowser) return;

        // Evita manipulación durante hidratación
        if ((window as any).__ng_hydrating__) return;

        // Efecto reactivo
        effect(() => {
            if (!this.pageDataLoaded()) return;
            this.initializeStickyFeatures();
        });

        // Handlers de resize
        this.boundResizeHandler = () => {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(() => {
                this.initializeStickyFeatures();
            }, 250);
        };

        window.addEventListener('resize', this.boundResizeHandler);
        window.addEventListener('mouseup', this.boundResizeHandler);

        this.boundVisibilityHandler = () => {
            if (document.visibilityState === 'visible') {
                // En vez de window.dispatchEvent(new Event('scroll'));
                // Usamos un ligero setTimeout para dar margen a que el ChangeDetectorRef
                // de layout.component.ts haya reconstruido el HTML
                setTimeout(() => {
                    this.initializeStickyFeatures();
                    // Opcional: forzar scroll después de inicializar si es requerido
                    window.dispatchEvent(new Event('scroll'));
                }, 50);
            }
        };
        document.addEventListener(
            'visibilitychange',
            this.boundVisibilityHandler
        );
    }

    ngOnDestroy(): void {
        if (!this.isBrowser) return;

        window.removeEventListener('resize', this.boundResizeHandler);
        window.removeEventListener('mouseup', this.boundResizeHandler);

        document.removeEventListener(
            'visibilitychange',
            this.boundVisibilityHandler
        );

        if (this.boundScrollHandler) {
            window.removeEventListener('scroll', this.boundScrollHandler);
        }
    }

    // -------------------------------------------------------
    // 🔧 Inicialización principal
    // -------------------------------------------------------
    private initializeStickyFeatures(): void {
        if (!this.isBrowser) return;
        setTimeout(() => {
            const excludedList = this.excludedHeaders();

            const isExcluded = excludedList.some(
                (selector) => !!document.querySelector(selector)
            );

            if (isExcluded) {
                document.body.classList.remove('header_sticky');
                const container = document.querySelector(
                    '.header-desktop-container'
                ) as HTMLElement;
                if (container) container.style.opacity = '1';
                return;
            }

            const engineEnabled = environment.booking_engine_enabled;
            const headerSelectors = engineEnabled
                ? ['#headerBookShow', '#headerCenterBookShow']
                : ['#headerCenterBookShow'];

            const validator = headerSelectors.some((sel) =>
                document.querySelector(sel)
            );

            const hasTopSlider =
                !!document.querySelector('lib-babylon-top-slider') ||
                !!document.querySelector('lib-babylon-top-slider-thumbs') ||
                !!document.querySelector('lib-babylon-top-slider-video');

            // Estilos condicionados
            this.applyHeaderClasses();

            if (this.dynamicHeaderPage()) {
                this.initScrollMode(validator, hasTopSlider);
            } else {
                this.initStaticMode(validator);
            }
        }, 0);
    }

    // -------------------------------------------------------
    // 📌 Modo dinámico (con scroll)
    // -------------------------------------------------------
    private initScrollMode(validator: boolean, hasTopSlider: boolean): void {
        const container = document.querySelector(
            '.header-desktop-container'
        ) as HTMLElement;
        if (container) container.style.opacity = '1';

        if (this.boundScrollHandler) {
            window.removeEventListener('scroll', this.boundScrollHandler);
        }

        this.boundScrollHandler = this.manageScroll.bind(
            this,
            validator,
            hasTopSlider
        );
        window.addEventListener('scroll', this.boundScrollHandler);

        window.dispatchEvent(new Event('scroll'));
    }

    // -------------------------------------------------------
    // 📌 Modo estático (sin scroll dependiente)
    // -------------------------------------------------------
    private initStaticMode(validator: boolean): void {
        if (this.boundScrollHandler) {
            window.removeEventListener('scroll', this.boundScrollHandler);
        }

        document.body.classList.add('header_sticky');

        document
            .querySelectorAll('.fixed_header')
            ?.forEach((el) =>
                el.classList.add('header-sticky', 'cs_gescout_sticky')
            );

        if (validator) {
            document
                .querySelector('.babylon__engine')
                ?.classList.add('engine-sticky');
            this.moveEngineTo('.babylon__header__box');
        }

        const header = document.querySelector(
            '.header-desktop-container'
        ) as HTMLElement;
        if (header) header.style.opacity = '1';
    }

    // -------------------------------------------------------
    // ♻ Scroll principal
    // -------------------------------------------------------
    private manageScroll(validator: boolean, hasTopSlider: boolean): void {
        if (!this.isBrowser) return;

        if (window.scrollY > 100) {
            document
                .querySelectorAll('.fixed_header')
                ?.forEach((el) =>
                    el.classList.add('header-sticky', 'cs_gescout_sticky')
                );

            if (validator) {
                document
                    .querySelector('.babylon__engine')
                    ?.classList.add('engine-sticky');
                this.moveEngineTo('.babylon__header__box');
            }

            document.body.classList.add('header_sticky');
            hasTopSlider
                ? document.body.classList.add('has_topslider')
                : document.body.classList.remove('has_topslider');
        } else {
            document
                .querySelectorAll('.fixed_header')
                ?.forEach((el) =>
                    el.classList.remove('header-sticky', 'cs_gescout_sticky')
                );

            if (validator) {
                document
                    .querySelector('.babylon__engine')
                    ?.classList.remove('engine-sticky');
                this.moveEngineTo('.top-container');
            }

            document.body.classList.remove('header_sticky');
            document.body.classList.remove('has_topslider');
        }

        // Animación de gallery
        if (window.innerWidth >= 1024) {
            const el = document.getElementById('babylon__gallery-slider');
            const position = (el?.offsetTop ?? 200) - 200;
            const gallery = document.querySelector(
                '.babylon__gallery-slider .babylon__gallery-slider--content'
            );

            if (!gallery) return;

            window.scrollY >= position
                ? gallery.classList.remove('no-active')
                : gallery.classList.add('no-active');
        }
    }

    // -------------------------------------------------------
    // 🔧 Helpers seguros para el engine
    // -------------------------------------------------------
    private moveEngineTo(targetSelector: string): void {
        const engine = document.querySelector('lib-babylon-engine');
        const target = document.querySelector(targetSelector);

        // Elementos no existen
        if (!engine || !target) return;

        // ❌ NO mover si es una hydration shell vacía
        if (engine.childNodes.length === 0 && engine.innerHTML.trim() === '') {
            return;
        }

        // ❌ NO mover si no es un nodo válido
        if (!(engine instanceof Node) || engine.nodeType !== 1) {
            return;
        }

        try {
            target.appendChild(engine);
        } catch (err) {
            console.warn('No se pudo mover lib-babylon-engine:', err);
        }
    }

    private applyHeaderClasses(): void {
        if (document.querySelector('#headerCenterBookShow')) {
            document
                .querySelector('.babylon__engine')
                ?.classList.add('header-center-bookshow-engine');
        }

        if (document.querySelector('#headerBookShow')) {
            document
                .querySelector('.babylon__engine')
                ?.classList.add('header-bookshow-engine');
        }
    }
}
